import React, { useState, useEffect, useContext } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import FirebaseOrder from './FirebaseOrder';
import AddressSheet from '../../components/AddressSheet';
import { LocalizationContext } from '../../localization/LocalizationContext';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { fs } from '../../firebase';
import { getUserPhoneNumber } from '../../utils/userUtils';
import { getMetaBySyrveKey } from '../../config/restaurantMeta';
import {
  ORDER_TYPES,
  getOrderTypeHint,
  getOrderTypeIcon,
  getOrderTypeLabel,
} from '../../utils/orderFulfillment';
import '../../css/FinalCheckout.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const RESTAURANT_COLORS = {
  'poshnosh': '#ef4444',
  'copacabana': '#3b82f6',
  'aromi italiani': '#22c55e',
  'symposium': '#f97316',
  'nadimi': '#a16207',
  'cave': '#8b5cf6',
};

const getRestaurantColor = (restaurantName) => {
  if (!restaurantName) return '#9ca3af';
  return RESTAURANT_COLORS[restaurantName.toLowerCase()] || '#9ca3af';
};

const ORDER_TYPE_BY_TAB = {
  Delivery: ORDER_TYPES.DELIVERY,
  Pickup: ORDER_TYPES.SELF_PICKUP,
  DineIn: ORDER_TYPES.DINE_IN,
};

const FinalCheckout = ({ totalPrice, selectedProducts, productQuantities, confirmedLocation, onEditAddress }) => {
  const { translations } = useContext(LocalizationContext);
  const [recipientName, setRecipientName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [house, setHouse] = useState('');
  const [floor, setFloor] = useState('');
  const [orderType, setOrderType] = useState('Delivery');
  const [paymentMethod, setPaymentMethod] = useState('online');
  const [editingUser, setEditingUser] = useState(false);
  const [selectedTable, setSelectedTable] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('asap');
  const [comment, setComment] = useState('');
  const [activeLocation, setActiveLocation] = useState(confirmedLocation);
  const [showAddressPicker, setShowAddressPicker] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      setRecipientName(user.displayName || '');
      const userDocRef = doc(fs, 'users', user.uid);
      getDoc(userDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            const userData = docSnap.data();
            setPhoneNumber(getUserPhoneNumber(userData) || '');
          }
        })
        .catch((error) => {
          console.error('Error fetching user data:', error);
        });
    }
  }, []);

  const restaurantKeys = Object.keys(selectedProducts).filter(k => selectedProducts[k].length > 0);

  const canonicalOrderType = ORDER_TYPE_BY_TAB[orderType] || ORDER_TYPES.DELIVERY;
  const orderReceipt = canonicalOrderType;
  const pickupFrom = restaurantKeys
    .map((key) => getMetaBySyrveKey(key)?.name || key)
    .join(', ');
  const tableNumber = selectedTable ? selectedTable.replace('table-', '') : '';

  const assembledOrderData = {
    orderType: canonicalOrderType,
    recipientName,
    phoneNumber,
    destination: activeLocation,
    floor,
    house,
    deliveryTime: 'Now',
    comment: comment.trim(),
    paymentMethod,
    ...(canonicalOrderType === ORDER_TYPES.SELF_PICKUP && pickupFrom ? { pickupFrom } : {}),
    ...(canonicalOrderType === ORDER_TYPES.DINE_IN && selectedTable ? { tableNum: selectedTable } : {}),
  };

  const fulfilmentDetail = (() => {
    if (canonicalOrderType === ORDER_TYPES.DELIVERY) return activeLocation?.address || '';
    if (canonicalOrderType === ORDER_TYPES.SELF_PICKUP) {
      if (!pickupFrom) return '';
      return `${translations?.deliveryPage?.PickupFrom || 'Pickup from'}: ${pickupFrom}`;
    }
    if (!tableNumber) return '';
    return `${translations?.deliveryPage?.tableLabel || 'Table'} ${tableNumber}`;
  })();

  const getRestaurantSubtotal = (products) => {
    let sub = 0;
    products.forEach(product => {
      const quantity = productQuantities[product.id] || 1;
      const modifiersTotal = (product.modifiers || []).reduce(
        (sum, mod) => sum + (mod.price || 0) * (mod.amount || 1),
        0
      );
      sub += (product.price + modifiersTotal) * quantity;
    });
    return sub;
  };

  return (
    <div className="fc-page">
      <div className="fc-header">
        <button className="fc-back-btn" onClick={() => window.history.back()}>←</button>
        <h1 className="fc-title">{translations?.deliveryPage?.FinalCheckout || 'Final Checkout'}</h1>
        <div style={{ width: 40 }} />
      </div>

      <div className="fc-card">
        {/* Map Section - Only for Delivery */}
        {orderType === 'Delivery' && (
          <>
            <div className="fc-map-section">
              <MapContainer
                key={`${activeLocation.lat},${activeLocation.lng}`}
                center={[activeLocation.lat, activeLocation.lng]}
                zoom={14}
                scrollWheelZoom={false}
                dragging={false}
                zoomControl={false}
                style={{ height: '100%', width: '100%', borderRadius: '12px' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap contributors'
                />
                <Marker position={[activeLocation.lat, activeLocation.lng]}>
                  <Popup>{activeLocation.address}</Popup>
                </Marker>
              </MapContainer>
            </div>

            {/* Delivery Address Card */}
            <div className="fc-address-card">
              <div className="fc-section-label" style={{ color: '#ef4444' }}>{translations?.deliveryPage?.DeliveryAddress || 'DELIVERY ADDRESS'}</div>
              <div className="fc-address-text">{activeLocation.address}</div>
              <div className="fc-address-actions">
                <button className="fc-saved-addresses-btn" onClick={() => setShowAddressPicker(true)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/><path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"/>
                  </svg>
                  {translations?.deliveryPage?.SavedAddresses || 'Addresses'}
                </button>
                <button className="fc-edit-btn" onClick={onEditAddress}>{translations?.deliveryPage?.Edit || 'Edit'}</button>
              </div>
            </div>
          </>
        )}

        {/* Delivery/Pickup/DineIn Toggle */}
        <div className="fc-toggle-group">
          <button
            className={`fc-toggle-btn ${orderType === 'Delivery' ? 'active' : ''}`}
            onClick={() => setOrderType('Delivery')}
          >
            {getOrderTypeIcon(ORDER_TYPES.DELIVERY)} {getOrderTypeLabel(ORDER_TYPES.DELIVERY, translations)}
          </button>
          <button
            className={`fc-toggle-btn ${orderType === 'Pickup' ? 'active' : ''}`}
            onClick={() => setOrderType('Pickup')}
          >
            {getOrderTypeIcon(ORDER_TYPES.SELF_PICKUP)} {getOrderTypeLabel(ORDER_TYPES.SELF_PICKUP, translations)}
          </button>
          <button
            className={`fc-toggle-btn ${orderType === 'DineIn' ? 'active' : ''}`}
            onClick={() => setOrderType('DineIn')}
          >
            {getOrderTypeIcon(ORDER_TYPES.DINE_IN)} {getOrderTypeLabel(ORDER_TYPES.DINE_IN, translations)}
          </button>
        </div>

        <div className={`fc-fulfilment-card fc-fulfilment-card--${canonicalOrderType}`}>
          <span className="fc-fulfilment-icon">{getOrderTypeIcon(canonicalOrderType)}</span>
          <div className="fc-fulfilment-body">
            <div className="fc-fulfilment-label">{translations?.deliveryPage?.OrderMethod || 'ORDER METHOD'}</div>
            <div className="fc-fulfilment-value">{getOrderTypeLabel(canonicalOrderType, translations)}</div>
            <div className="fc-fulfilment-hint">{getOrderTypeHint(canonicalOrderType, translations)}</div>
            {fulfilmentDetail && <div className="fc-fulfilment-detail">{fulfilmentDetail}</div>}
          </div>
        </div>

        {/* User Information Card */}
        <div className="fc-user-card">
          <div className="fc-user-header">
            <h3>{translations?.deliveryPage?.UserInformation || 'User Information'}</h3>
            <button
              className="fc-user-edit-link"
              onClick={() => setEditingUser(!editingUser)}
            >
              {editingUser ? translations?.deliveryPage?.Done || 'Done' : translations?.deliveryPage?.Edit || 'Edit'}
            </button>
          </div>

          {editingUser ? (
            <div className="fc-user-edit-form">
              <div className="fc-form-group">
                <label className="fc-form-label">{translations?.deliveryPage?.RecipientName || 'FULL NAME'}</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="fc-form-input"
                />
              </div>
              <div className="fc-form-group">
                <label className="fc-form-label">{translations?.deliveryPage?.PhoneNumber || 'MOBILE NUMBER'}</label>
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="fc-form-input"
                />
              </div>
            </div>
          ) : (
            <div className="fc-user-display">
              <div className="fc-user-row">
                <span className="fc-user-icon">👤</span>
                <div className="fc-user-info">
                  <div className="fc-user-label">{translations?.deliveryPage?.RecipientName || 'FULL NAME'}</div>
                  <div className="fc-user-value">{recipientName || translations?.deliveryPage?.NotSet || 'Not set'}</div>
                </div>
              </div>
              <div className="fc-user-row">
                <span className="fc-user-icon">📱</span>
                <div className="fc-user-info">
                  <div className="fc-user-label">{translations?.deliveryPage?.PhoneNumber || 'MOBILE NUMBER'}</div>
                  <div className="fc-user-value">{phoneNumber || translations?.deliveryPage?.NotSet || 'Not set'}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Address Details - Only for Delivery */}
        {orderType === 'Delivery' && (
          <div className="fc-address-details">
            <h3 className="fc-section-heading">{translations?.deliveryPage?.AddressDetails || 'ADDRESS DETAILS'}</h3>
            <div className="fc-details-row">
              <div className="fc-details-field">
                <label className="fc-details-label">{translations?.deliveryPage?.HouseAptNumber || 'HOUSE/APT NUMBER'}</label>
                <input
                  type="text"
                  value={house}
                  onChange={(e) => setHouse(e.target.value)}
                  placeholder={translations?.deliveryPage?.houseExample || 'e.g. 12'}
                  className="fc-details-input"
                />
              </div>
              <div className="fc-details-field">
                <label className="fc-details-label">{translations?.deliveryPage?.Floor || 'FLOOR'}</label>
                <input
                  type="text"
                  value={floor}
                  onChange={(e) => setFloor(e.target.value)}
                  placeholder={translations?.deliveryPage?.floorExample || 'e.g. 4'}
                  className="fc-details-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Pickup/DineIn Time Selection */}
        {orderType !== 'Delivery' && (
          <div className="fc-address-details">
            <h3 className="fc-section-heading">
              {orderType === 'Pickup' ? translations?.deliveryPage?.PickupTime || 'PICKUP TIME' : translations?.deliveryPage?.ReservationTime || 'RESERVATION TIME'}
            </h3>
            <div className="fc-toggle-group">
              <button
                className={`fc-toggle-btn ${deliveryTime === 'asap' ? 'active' : ''}`}
                onClick={() => setDeliveryTime('asap')}
              >
                {translations?.deliveryPage?.AsSoonAsPossible || 'As soon as possible'}
              </button>
              <button
                className={`fc-toggle-btn ${deliveryTime === 'scheduled' ? 'active' : ''}`}
                onClick={() => setDeliveryTime('scheduled')}
              >
                {translations?.deliveryPage?.AtScheduledTime || 'At a specific time'}
              </button>
            </div>
          </div>
        )}

        {/* Table Selection - Only for DineIn */}
        {orderType === 'DineIn' && (
          <div className="fc-address-details">
            <h3 className="fc-section-heading">{translations?.deliveryPage?.SelectTable || 'SELECT TABLE'}</h3>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              className="fc-details-input"
            >
              <option value="">{translations?.deliveryPage?.AtTheTable || 'Choose a table...'}</option>
              <option value="table-1">{translations?.deliveryPage?.tableLabel || 'Table'} 1 (2 {translations?.deliveryPage?.seats || 'seats'})</option>
              <option value="table-2">{translations?.deliveryPage?.tableLabel || 'Table'} 2 (4 {translations?.deliveryPage?.seats || 'seats'})</option>
              <option value="table-3">{translations?.deliveryPage?.tableLabel || 'Table'} 3 (6 {translations?.deliveryPage?.seats || 'seats'})</option>
              <option value="table-4">{translations?.deliveryPage?.tableLabel || 'Table'} 4 (8 {translations?.deliveryPage?.seats || 'seats'})</option>
            </select>
          </div>
        )}

        <div className="fc-address-details">
          <h3 className="fc-section-heading">{translations?.deliveryPage?.CommentForTheRestaurant || 'Comment for the Restaurant'}</h3>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={translations?.deliveryPage?.YourWishes || 'Your wishes...'}
            className="fc-comment-input"
            rows={3}
            maxLength={500}
          />
        </div>

        {/* Order Summary */}
        <div className="fc-order-summary">
          <h3 className="fc-section-heading">{translations?.deliveryPage?.OrderSummary || 'ORDER SUMMARY'}</h3>
          {restaurantKeys.map((orgId) => {
            const products = selectedProducts[orgId];
            const meta = getMetaBySyrveKey(orgId);
            const restaurantName = meta?.name || orgId;
            const subtotal = getRestaurantSubtotal(products);
            const dotColor = getRestaurantColor(restaurantName);

            return (
              <div key={orgId} className="fc-order-item">
                <span className="fc-order-dot" style={{ backgroundColor: dotColor }}></span>
                <span className="fc-order-name">{restaurantName}</span>
                <span className="fc-order-price">₾{subtotal.toFixed(2)}</span>
              </div>
            );
          })}

          <div className="fc-service-fee">
            <span>{translations?.deliveryPage?.ServiceFeeDelivery || 'Service Fee & Delivery'}</span>
            <span>{translations?.deliveryPage?.Included || 'Included'}</span>
          </div>
        </div>

        {/* Payment Method */}
        <div className="fc-payment-section">
          <h3 className="fc-section-heading">{translations?.deliveryPage?.PaymentMethod || 'PAYMENT METHOD'}</h3>
          <div className="fc-payment-options">
            <button
              className={`fc-payment-btn ${paymentMethod === 'online' ? 'active' : ''}`}
              onClick={() => setPaymentMethod('online')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 5m0 3a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3z" />
                <path d="M3 10l18 0" />
                <path d="M7 15l.01 0" />
                <path d="M11 15l2 0" />
              </svg>
              {translations?.deliveryPage?.OnlinePayment || 'Online Payment'}
            </button>
          </div>
        </div>
      </div>

      {/* Order Button */}
      <FirebaseOrder
        orderData={assembledOrderData}
        selectedProducts={selectedProducts}
        productQuantities={productQuantities}
        totalPrice={totalPrice}
        orderReceipt={orderReceipt}
      />

      {showAddressPicker && (
        <AddressSheet
          onClose={() => setShowAddressPicker(false)}
          onSelectAddress={(loc) => {
            setActiveLocation(loc);
            setShowAddressPicker(false);
          }}
          currentAddress={activeLocation.address}
        />
      )}
    </div>
  );
};

export default FinalCheckout;
